/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./Utils',
	'./VisRecommenderDelegator',
	'../../../api/impl/Visualization',
	'../../../widgets/livewidget/models/LiveWidgetModel'
], function (Utils, VisRecommenderDelegator, Visualization, LiveWidgetModel) {
	'use strict';

	class VisualizationSmartsRecommender {

		constructor({ features }) {
			this.features = features;
			this.transaction = features['Transaction'];
			this.state = features['state.internal'];
			this.logger = features['Logger'];
			this.visDefinitions = this.features['VisDefinitions'];

			this.moserDataSources = this.features['DataSources.moser'];
			this._visRecommenderDelegator = new VisRecommenderDelegator({
				dataSources: this.features['DataSources'],
				moserDataSources: this.moserDataSources,
				visDefinitions: this.visDefinitions,
				dashboardApi: this.features['Dashboard.API'],
				logger: this.logger
			});
		}

		destroy() {
			this.features = null;
			this.transaction = null;
			this.state = null;
			this.logger = null;
			this.visDefinitions = null;
			this.creationTransactionToken = null;
			this._visRecommenderDelegator = null;
		}

		getAPI(type) {
			if (type === 'deprecated') {
				return this._deprecatedAPI;
			}

			return this._api;
		}

		isValidDataSource(visualization){
			return visualization.getDataSource() && visualization.getDataSource().getState() !== 'error';
		}

		getRecommendedVisualizations(visualization) {
			if (this.isValidDataSource(visualization) && visualization.getSlots().isMappingComplete()) {
				return this.recommendAlternateVisualizations(visualization)
					.then(recommendations => {
						return recommendations.map(recommendation => this.visDefinitions.getById(recommendation.visId));
					});
			} else {
				return Promise.resolve([]);
			}
		}


		getRecommendedTitle(visualization) {
			if(this.isValidDataSource(visualization)){
				return this._visRecommenderDelegator.getRecommendInfo(visualization).then(function (info) {
					return info.label;
				}).catch(error => {
					return Promise.reject(new Error(error));
				});
			} else {
				return Promise.resolve();
			}
		}

		setType(visualization, type, transactionToken) {
			const subTransaction = this.transaction.startTransaction(transactionToken);
			const newDefinition = this.visDefinitions.getByType(type);
			if (newDefinition) {
				if (!visualization.isTypeLocked()) {
					visualization.lockType(subTransaction);
				}
				if (this.isValidDataSource(visualization)){
					return this._visRecommenderDelegator.recommendBindingsForSelectedViz(visualization, newDefinition.getId()).then(recommendation => {
						this._applyRecommendationToVisualization(visualization, recommendation, subTransaction);
					}).finally(() => {
						this.transaction.endTransaction(subTransaction);
					});
				} else {
					visualization.setType(type, subTransaction);
					this.transaction.endTransaction(subTransaction);
					return Promise.resolve();
				}

			}
			throw new Error('Invalid definition');
		}

		addDataItems(visualization, dataItemSpecList, transactionToken) {
			const dataSource = visualization.getDataSource();

			if (!dataSource) {
				throw new Error('You must set a datasource in the visualization feature first');
			}

			const columnIds = dataItemSpecList.map(dataItemSpec => dataItemSpec.columnId);
			const subTransactionToken = this.transaction.startTransaction(transactionToken);
			if (visualization.isTypeLocked()) {
				return this._visRecommenderDelegator.recommendBindingsForNewColumns(visualization, columnIds).then(recommendation => {
					this._applyRecommendationToVisualization(visualization, recommendation, subTransactionToken);
				}).finally(() => {
					this.transaction.endTransaction(subTransactionToken);
				});
			} else {
				return this._visRecommenderDelegator.recommendBestAlternateVisualization(visualization, columnIds).then(recommendation => {
					if(this.features.LiveWidgetSegment) {
						this.features.LiveWidgetSegment.track({ visualization: visualization, info: recommendation, category: 'fromRecommendation' });
					}
					this._applyRecommendationToVisualization(visualization, recommendation, subTransactionToken);
				}).finally(() => {
					this.transaction.endTransaction(subTransactionToken);
				});
			}
		}

		recommendBestVisualization(visualization, transactionToken) {
			if (this.isValidDataSource(visualization)) {
				const subTransaction = this.transaction.startTransaction(transactionToken);
				if (visualization.isTypeLocked()) {
					visualization.unlockType(subTransaction);
				}
				return this._visRecommenderDelegator.recommendBestAlternateVisualization(visualization).then(recommendation => {
					if (this.features.LiveWidgetSegment) {
						this.features.LiveWidgetSegment.track({ visualization: visualization, info: recommendation, category: 'fromRecommendation' });
					}
					this._applyRecommendationToVisualization(visualization, recommendation, subTransaction);
				}).finally(() => {
					this.transaction.endTransaction(subTransaction);
				});
			}
			return Promise.resolve();
		}

		recommendAlternateVisualizations(visualization) {
			let result;
			if (this.isValidDataSource(visualization)) {
				const dataSource = visualization.getDataSource();
				const module = this.moserDataSources.getModule(dataSource.getId());
				const columnIds = Utils.getUnboundColumnsIds(visualization);

				result = this._visRecommenderDelegator.recommendAlternateVisualizations(columnIds, {
					assetId: module.getAssetId(),
					sourceType: module.getSourceType(),
					module: module
				});
			} else {
				result = Promise.resolve([]);
			}
			return result;
		}


		recommendAlternateVisualizationsAsWidgetSpecs(visualization, baseWidgetSpec) {
			return this.recommendAlternateVisualizations(visualization)
				.then(recommendations => this._transformWidgetRecommendationsToLiveWidgetSpecs(recommendations, baseWidgetSpec));
		}


		recommendRelatedVisualizations(visualization) {
			let result;
			if (this.isValidDataSource(visualization)) {
				const dataSource = visualization.getDataSource();
				const columnIds = Utils.getUnboundColumnsIds(visualization);
				const topBottomItems = Utils.getTopBottomItems(visualization);
				const filterItems = Utils.getFilterItems(visualization);
				result = this.recommendRelatedVisualizationsFromColumnIds(dataSource.getId(), columnIds,  [...topBottomItems, ...filterItems]);
			} else {
				result = Promise.resolve([]);
			}
			return result;
		}

		recommendAlternateVisualizationsFromColumnIds(dataSourceId, columnIds) {
			const module = this.moserDataSources.getModule(dataSourceId);
			return this._visRecommenderDelegator.recommendAlternateVisualizations(columnIds, {
				assetId: module.getAssetId(),
				sourceType: module.getSourceType(),
				module: module
			});

		}

		recommendRelatedVisualizationsFromColumnIds(dataSourceId, columnIds, filters, includeBestVisForTargetFields) {
			const module = this.moserDataSources.getModule(dataSourceId);
			return this._visRecommenderDelegator.recommendRelatedVisualizations(columnIds, {
				assetId: module.getAssetId(),
				sourceType: module.getSourceType(),
				module: module,
				filters: filters,
				includeBestVisForTargetFields: !!includeBestVisForTargetFields,
				moserDataSources: this.moserDataSources,
				visualization: this.visualization
			});
		}

		// deprecated interface -- called by explore
		recommendRelatedVisualizationsAsWidgetSpecs(visualization, baseWidgetSpec) {
			return this.recommendRelatedVisualizations(visualization)
				.then(recommendations => this._transformWidgetRecommendationsToLiveWidgetSpecs(recommendations, baseWidgetSpec));
		}

		recommendCompareVisualizationsAsWidgetSpecs(visualization, widgetSpec){
			let result;
			if (this.isValidDataSource(visualization)){
				const dataSource = visualization.getDataSource();
				const module = this.moserDataSources.getModule(dataSource.getId());
				const options = {};
				options.module = module;
				options.columnIds =  Utils.getUnboundColumnsIds(visualization);
				options.widgetSpec = widgetSpec;
				options.visualization = visualization;
				options.assetId = dataSource.getAssetId();
				options.sourceType = dataSource.getType();
				options.moserDataSources = this.moserDataSources;
				result = this._visRecommenderDelegator.recommendCompareVisualizations(options.columnIds, options);
			} else {
				result = Promise.resolve([]);
			}

			return result;
		}


		_transformWidgetRecommendationsToLiveWidgetSpecs(recommendations, basewidgetSpec) {
			const promises = [];
			const results = [];
			recommendations.forEach(recommendation => {
				basewidgetSpec = JSON.parse(JSON.stringify(basewidgetSpec));
				// Shoud be done throught the API -- but we need to be able to easily create a content API
				basewidgetSpec.name = recommendation.label;

				// Should be done through the filter API once it is ready
				if (recommendation.filters && recommendation.filters.length > 0) {
					basewidgetSpec.localFilters = recommendation.filters;
				}

				const widgetModelCopy = new LiveWidgetModel(basewidgetSpec);
				const visualization = new Visualization({
					doNotRegisterDataSourceUsage: true,
					content: this.content,
					features: {
						'Models.internal': {
							getWidgetModel: () => {
								return widgetModelCopy;
							}
						},
						'Dashboard.API': this.features['Dashboard.API'],
						'Dashboard.Logger': this.features['Logger'],
						'Dashboard.Colors': this.features['Colors'],
						'Dashboard.Transaction': this.features['Transaction'],
						'Dashboard.DataSources': this.features['DataSources'],
						'Dashboard.internal': this.features['Dashboard.internal'],
						'Dashboard.VisDefinitions': this.features['VisDefinitions'],
						'Dashboard.VisDefinitions.internal': this.features['VisDefinitions.internal'],
						'Dashboard.DashboardSettings': this.features['DashboardSettings']
					}
				});
				promises.push(visualization.initialize().then(() => {
					visualization.unlockType();
					this._applyRecommendationToVisualization(visualization, recommendation);

					// strip the thumbnailId from the base model
					const spec = widgetModelCopy.toJSON([], ['thumbnailId']);

					// return recommendation specs without ids will generate ids at the addition time
					delete spec.id;

					// TODO -- why this ?
					// the conversation expects a recommenderInfo for some reason..
					spec.recommenderInfo =  {
						rtitle: recommendation.title,
						description: recommendation.description,
						label: recommendation.label
					};

					results.push({
						name: widgetModelCopy.get('name'),
						spec: spec
					});
				}));

			});
			console.log('recommendations... ', results);

			return Promise.all(promises).then(() => { return results; });
		}

		_applyRecommendationToVisualization(visualization, recommendation, transactionToken) {

			const subTransaction = this.transaction.startTransaction(transactionToken);

			// Get the original list of dataItems mapped to slots before the visId change.
			const originalSlots = visualization.getSlots();
			const originalMappedDataItemIdList = originalSlots.getMappingInfoList().map((diInfo) => diInfo.dataItem && diInfo.dataItem.getId());

			// Check if we are changing the viz
			if (recommendation.visId !== visualization.getDefinition().getId()) {
				const newDef = this.visDefinitions.getById(recommendation.visId);
				visualization.setType(newDef.getType(), subTransaction);
			}

			const slots = visualization.getSlots();

			let unmappedDataItemList = [];
			const mappedDataItemListBySlot = {};
			slots.getDataItemList().forEach((dataItem) => {
				const mapping = slots.getMappingInfo(dataItem.getId());
				if (!mapping) {
					unmappedDataItemList.push(dataItem);
				} else {
					const slotId = mapping.slot.getId();
					if (!mappedDataItemListBySlot[slotId]) {
						mappedDataItemListBySlot[slotId] = [];
					}
					mappedDataItemListBySlot[slotId].push(dataItem);
				}
			});
			const recommendedColumns = {};
			for (let slotName in recommendation.slots) {
				recommendation.slots[slotName].forEach(value => {
					recommendedColumns[value] = true;
				});
			}
			// Check if the recommender has changed some mapping to a different slot. If so, we move the dataItem to the unmapped datsaItems so that it can be
			// picked by another slot
			const slotList = slots.getSlotList();
			slotList.forEach((slot) => {
				const name = slot.getId();
				const dataItems = slot.getDataItemList(true);
				dataItems.forEach(dataItem => {
					if (!recommendation.slots[name] || !recommendation.slots[name].find((columnId) => dataItem.getColumnId() === columnId)) {
						unmappedDataItemList.push(dataItem);
					}
				});
			});

			const newMappingBySlot = {};
			slotList.forEach((slot) => {
				const name = slot.getId();
				const slotId = slot.getId();
				const dataItemsToAdd = [];
				if(recommendation.slots[name]) {
					recommendation.slots[name].forEach((columnId) => {

						let dataItem;
						const filterDataItems = di => {
							if (!dataItem && columnId === di.getColumnId()) {
								dataItem = di;
								return false;
							}
							return true;
						};

						// 1 - Search the previous mapped dataitems to this slot (if they exist)
						if (mappedDataItemListBySlot[slotId]) {
							// We filter to exclude the matched dataitems as we find them
							mappedDataItemListBySlot[slotId] = mappedDataItemListBySlot[slotId].filter(filterDataItems);
						}

						// 2 - Search the unmapped data items
						if (!dataItem) {
							// Search the unmapped list
							unmappedDataItemList = unmappedDataItemList.filter(filterDataItems);
						}

						// 3 - New column eing added, create a dataImte
						if (!dataItem) {
							dataItem = slots.createDataItems([{ columnId: columnId }], subTransaction)[0];
						}

						dataItemsToAdd.push(dataItem.getId());
					});
				} else {
					// slots which are not recommendable should maintain the original mappings
					// for example, heat slot for crosstab and table
					const unmappedSlot = slots.getSlot(slotId);
					const isHidden = unmappedSlot.getDefinition().isHidden();
					if (isHidden) {
						unmappedSlot.getDataItemList().forEach((dataItem) => {
							if (unmappedDataItemList.find(unmappedDataItem => unmappedDataItem.getId() === dataItem.getId())) {
								dataItemsToAdd.push(dataItem.getId());
								unmappedDataItemList = unmappedDataItemList.filter(unmappedDataItem => unmappedDataItem.getId() !== dataItem.getId());
							}
						});
					}
				}

				const dataItemList = slot.getDataItemList(true);

				const dataItemsToRemove = dataItemList.filter(dataItem => {
					return dataItem.isColumnUnavailable();
				});

				if(dataItemsToRemove.length > 0) {
					slot.removeDataItems(dataItemsToRemove.map(dataItem => dataItem.getId()), subTransaction);
				}

				newMappingBySlot[slotId] = dataItemsToAdd;
				slot.removeDataItemsMapping(dataItemList.map(dataItem => dataItem.getId()), subTransaction);

			});


			//Place slots which can generate measure groups at the end.
			//This means when a measure group is generated in addDataItemsMappings,
			//any other data items in the same slot will already exist, so it can be
			//placed in the correct position.
			const slotIds = Object.keys(newMappingBySlot)
				.map(id => slots.getSlot(id))
				.sort((slotA, slotB) => {
					const a = slotA.getDefinition().getProperty('multiMeasure') ? 1 : -1,
						b = slotB.getDefinition().getProperty('multiMeasure') ? 1 : -1;
					return a - b;
				})
				.map(slot => slot.getId());

			// Add all the new mappings
			slotIds.forEach(
				id => {
					if (newMappingBySlot[id].length > 0) {
						slots.getSlot(id).addDataItemsMapping(newMappingBySlot[id], -1, subTransaction);
					}
				}
			);

			const mappingInfoList = slots.getMappingInfoList();
			// remove all leftover top bottom items if smarts returns top bottom items, such as for related-viz case
			if (recommendation.topBottom && recommendation.topBottom.length > 0) {
				recommendation.topBottom.forEach(({
					itemId,
					spec
				}) => {
					const mappingEntry = mappingInfoList.find(mappingInfoList => {
						return mappingInfoList.dataItem.getColumnId() === itemId;
					});

					if (mappingEntry) {
						const topBottom = {
							type: spec.type,
							value: spec.value
						};
						if (spec.context) {
							topBottom.context = {
								itemId: spec.context,
								aggregate: spec.aggregate
							};
						}
						mappingEntry.dataItem.setTopBottom(topBottom, subTransaction);
					}
				});
			}

			// Auto Group
			(recommendation.autoGroup || []).forEach(autoGroupItem => {
				const { itemId, groupSize } = autoGroupItem;
				if (autoGroupItem && groupSize) {
					const mappingEntry = mappingInfoList.find(mappingInfoList => {
						return mappingInfoList.dataItem.getColumnId() === itemId;
					});
					if (mappingEntry) {
						mappingEntry.dataItem.setBinning({
							bins: groupSize
						}, subTransaction);
					}
				}
			});

			// remove remaining unmapped multiMeasure data items
			if (unmappedDataItemList.length > 0) {
				const unmappedMultiMeasureDataItemList = unmappedDataItemList.filter(dataItem => dataItem.getColumnId() === '_multiMeasuresSeries');
				slots.deleteDataItems(unmappedMultiMeasureDataItemList.map( dataItem => dataItem.getId()), subTransaction);
			}

			//Compare the final mapped DataItem list to the original one.  Clean any dataItems that are no longer mapped.
			const mappedDataItemIdList = slots.getMappingInfoList().map((diInfo) => diInfo.dataItem && diInfo.dataItem.getId());
			const unmappedDataItemIdList = originalMappedDataItemIdList.filter((originalId) => {
				return (mappedDataItemIdList.indexOf(originalId) === -1);
			});
			if (unmappedDataItemIdList.length > 0) {
				slots.deleteDataItems(unmappedDataItemIdList, subTransaction);
			}

			this.transaction.endTransaction(subTransaction);
		}


	}

	return VisualizationSmartsRecommender;
});
